import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import axios from "axios";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { skedApi } from "../../api/_skedApi";
import { GeneratedSkedApiClient } from "../../api/generated-client";
import type {
  components,
  paths,
} from "../../api/generated/sked-api-openapi-types";
import { queryKeys } from "../../api/query-keys";
import { fetchCalendar } from "../../api/calendar";
import {
  moveQueueToDraft,
  moveScheduleToDraft,
  editDraft,
} from "../../api/post";
import { cancelPlannerPost } from "../../api/planner";
import {
  CalendarPostType,
  RequestPostsOptionsTypes,
} from "../../features/calendar-new/types";
import { standardizationPostData } from "../../utils/posts";
import { handleError } from "../../utils/handleError";
import collabApi from "../../api/collaboration";
import {
  ColCollaborator,
  ColComment,
  ColComments,
  ColCommentVisibility,
} from "../../components/Collaboration/types";
import { Post } from "../../shared/types/post";
import {
  getApprovalBoardPostAccountIds,
  toLegacyApprovalPost,
} from "./approval-post-adapters";
import {
  getApprovalBoardPostType,
  type ApprovalBoardPost,
} from "./approval-post-state";
import { getUser } from "../../selectors/commonSelectors";
import {
  getAccounts,
  getAccountsReady,
} from "../../selectors/skedCoreSelectors";
import { hasExternalApprovals } from "../../utils/feature-gates";
import { removeLocalStorageSkedApiToken } from "../../libs/storage/adapters";
import {
  filterApprovalLinksByAccountAccess,
  filterApprovalThemesByVisibleLinks,
} from "./approval-visibility";
import {
  getApprovalBoardStatusFilterFromQueryKey,
  indexApprovalBoardPosts,
  type ApprovalBoardPostsData,
} from "./approval-board-cache";

export type { ApprovalBoardPostsData } from "./approval-board-cache";

export interface ApprovalTheme {
  _id: string;
  name: string;
  companyName: string;
  imageUrl?: string;
  deleted?: boolean;
  adminId?: string;
  linkCount?: number;
}

// SKE-4056: per-recipient recurring review reminder config, one row per email
// in notifications.onReadyToReview that has opted in.
export interface ReviewReminder {
  email: string;
  enabled: boolean;
  frequency: "daily" | "weekly";
  days: string[]; // weekly only: 'mon'..'sun'
  hour: number; // 0-23, local to `timezone`
  minute: number; // 0-55 in 5-min steps (Knock constraint)
  timezone: string; // IANA tz, e.g. 'Australia/Melbourne'
  knockScheduleId?: string;
}

export interface ApprovalLink {
  _id: string;
  accountIds: string[];
  deleted?: boolean;
  linkName: string;
  showPostWithState: string;
  acceptState: string;
  rejectState: string;
  themeId?: string;
  adminId?: string;
  slugId?: string;
  isEnabled?: boolean;
  isConversationsEnabled?: boolean;
  isStatusChangesVisible?: boolean;
  isExternalNotesVisible?: boolean;
  isPostActivityVisible?: boolean;
  isMarkupEnabled?: boolean;
  isIdeasEnabled?: boolean;
  isSignoffsRequired?: boolean;
  userName?: string;
  notifications?: {
    onAccept: string[];
    onReject: string[];
    onReadyToReview?: string[];
  };
  reviewReminders?: ReviewReminder[];
  isReadOnly?: boolean;
}

export interface ApprovalBoardPostsRequest extends Omit<
  RequestPostsOptionsTypes,
  "start" | "end"
> {
  start: number;
  end: number;
  postContentTypes: string[];
  postStatusKey: string[] | null;
}

export type ApprovalBoardMode = "upcoming" | "drafts";
export {
  canSendApprovalPostToQueue,
  getApprovalBoardPostState,
  getApprovalBoardPostType,
} from "./approval-post-state";

interface ApprovalBoardDraftRow {
  _id: string;
  accountIds?: string[];
  schedule?: Partial<Post> & Record<string, unknown>;
}

export type CreateApprovalThemeInput = Omit<Partial<ApprovalTheme>, "_id">;
export type CreateApprovalLinkInput = Omit<
  Partial<ApprovalLink>,
  "_id" | "slugId"
>;

const EXTERNAL_THEMES_URL = "/rest/externalapprovalthemes";
const EXTERNAL_LINKS_URL = "/rest/externalapprovals";
const BOARD_FUTURE_MONTHS = 6;
const EXTERNAL_APPROVAL_LINKS_LIMIT = 99;
const EXTERNAL_APPROVAL_THEMES_LIMIT = 10;

type GeneratedApprovalLinkItem =
  components["schemas"]["ExternalApprovalLinkItem"];
type GeneratedApprovalThemeItem =
  components["schemas"]["ExternalApprovalThemeItem"];
type GeneratedApprovalLink = Omit<
  GeneratedApprovalLinkItem,
  "accounts" | "theme"
> & {
  _id?: string;
  accounts?: Array<{ id?: string; _id?: string }>;
  externalReviewLink?: string | null;
  id?: string;
  name?: string | null;
  theme?: { id?: string; name?: string | null } | string | null;
};

// notifications is mistyped as `boolean` in the generated OpenAPI types and
// reviewReminders isn't there at all; read both off the raw item at runtime
type GeneratedApprovalLinkExtras = {
  notifications?: ApprovalLink["notifications"];
  reviewReminders?: ReviewReminder[];
};
type GeneratedApprovalTheme = Omit<GeneratedApprovalThemeItem, "links"> & {
  _id?: string;
  id?: string;
  linkCount?: number;
  links?: { total?: number };
};
type PaginatedGeneratedResponse<TItem> = {
  page?: number;
  total?: number;
  items?: TItem[];
};

function getGeneratedSkedApiClient() {
  return new GeneratedSkedApiClient<paths>();
}

function normalizeApprovalLink(link: ApprovalLink): ApprovalLink {
  const normalizedLink = { ...link } as ApprovalLink & {
    states?: unknown;
  };
  delete normalizedLink.states;
  return {
    ...normalizedLink,
    accountIds: link.accountIds ?? [],
  };
}

function getSlugIdFromExternalReviewLink(
  externalReviewLink?: string
): string | undefined {
  if (!externalReviewLink) {
    return undefined;
  }

  const reviewPathMatch = externalReviewLink.match(/\/review\/([^/?#]+)/);
  if (reviewPathMatch?.[1]) {
    return reviewPathMatch[1];
  }

  return externalReviewLink;
}

function getGeneratedThemeId(theme: GeneratedApprovalLink["theme"]) {
  if (!theme) {
    return undefined;
  }

  if (typeof theme === "string") {
    return theme;
  }

  return theme.id;
}

function getGeneratedAccountIds(accounts?: GeneratedApprovalLink["accounts"]) {
  return (accounts ?? [])
    .map((account) => account._id ?? account.id)
    .filter((accountId): accountId is string => Boolean(accountId));
}

function normalizeGeneratedApprovalLink(
  link: GeneratedApprovalLink
): ApprovalLink {
  const {
    accounts,
    externalReviewLink,
    id,
    name,
    notifications,
    theme,
    userName,
    ...legacyLink
  } = link;
  void notifications; // stripped from legacyLink; real value read from extras
  const extras = link as unknown as GeneratedApprovalLinkExtras;

  return normalizeApprovalLink({
    ...legacyLink,
    _id: link._id ?? link.id ?? "",
    accountIds: link.accountIds ?? getGeneratedAccountIds(accounts),
    linkName: link.linkName ?? name ?? "",
    showPostWithState: link.showPostWithState ?? "",
    acceptState: link.acceptState ?? "",
    rejectState: link.rejectState ?? "",
    themeId: link.themeId ?? getGeneratedThemeId(theme) ?? undefined,
    slugId:
      link.slugId ??
      getSlugIdFromExternalReviewLink(externalReviewLink ?? undefined) ??
      undefined,
    userName: userName ?? undefined,
    // preserve notification recipients and reminder rows so the edit sheet can
    // pre-fill them (both are otherwise dropped/mistyped above)
    notifications: extras.notifications ?? undefined,
    reviewReminders: extras.reviewReminders ?? [],
  });
}

function normalizeGeneratedApprovalTheme(
  theme: GeneratedApprovalTheme
): ApprovalTheme {
  return {
    ...theme,
    _id: theme._id ?? theme.id ?? "",
    name: theme.name ?? "",
    companyName: theme.companyName ?? "",
    imageUrl: theme.imageUrl ?? undefined,
    linkCount: theme.linkCount ?? theme.links?.total ?? 0,
  };
}

function sortApprovalThemesByName(themes: ApprovalTheme[]) {
  return [...themes].sort((themeA, themeB) =>
    themeA.name.localeCompare(themeB.name)
  );
}

function sortApprovalLinksByName(links: ApprovalLink[]) {
  return [...links].sort((linkA, linkB) =>
    linkA.linkName.localeCompare(linkB.linkName)
  );
}

// The server reads the wall-clock date in the profile timezone, so the date
// must come from that zone, not the browser's, or the window can start late.
export function getApprovalBoardWindow(
  timezone?: string
): Pick<ApprovalBoardPostsRequest, "start" | "end"> {
  const today = (timezone ? moment.tz(timezone) : moment()).format(
    "YYYY-MM-DD"
  );
  const start = moment.utc(today, "YYYY-MM-DD").startOf("day").valueOf();
  const end = moment
    .utc(today, "YYYY-MM-DD")
    .add(BOARD_FUTURE_MONTHS, "months")
    .endOf("day")
    .valueOf();
  return { start, end };
}

async function fetchGeneratedApprovalsPages({
  path,
  limit,
}: {
  path: "/v2/external-approvals";
  limit: number;
}): Promise<GeneratedApprovalLink[]>;
async function fetchGeneratedApprovalsPages({
  path,
  limit,
}: {
  path: "/v2/external-approvals-themes";
  limit: number;
}): Promise<GeneratedApprovalTheme[]>;
async function fetchGeneratedApprovalsPages({
  path,
  limit,
}: {
  path: "/v2/external-approvals" | "/v2/external-approvals-themes";
  limit: number;
}): Promise<Array<GeneratedApprovalLink | GeneratedApprovalTheme>> {
  const generatedClient = getGeneratedSkedApiClient();
  const items: Array<GeneratedApprovalLink | GeneratedApprovalTheme> = [];
  let page = 1;
  let total = 0;

  do {
    const data =
      path === "/v2/external-approvals"
        ? await generatedClient.unwrap<
            PaginatedGeneratedResponse<GeneratedApprovalLink>
          >(
            await generatedClient.client.GET("/v2/external-approvals", {
              params: { query: { limit, page } },
            })
          )
        : await generatedClient.unwrap<
            PaginatedGeneratedResponse<GeneratedApprovalTheme>
          >(
            await generatedClient.client.GET("/v2/external-approvals-themes", {
              params: { query: { limit, page } },
            })
          );

    const pageItems = data.items ?? [];
    items.push(...pageItems);
    total = data.total ?? items.length;
    page += 1;

    if (pageItems.length === 0) {
      break;
    }
  } while (items.length < total);

  return items;
}

export async function listApprovalThemes(): Promise<ApprovalTheme[]> {
  const items = await fetchGeneratedApprovalsPages({
    path: "/v2/external-approvals-themes",
    limit: EXTERNAL_APPROVAL_THEMES_LIMIT,
  });
  return sortApprovalThemesByName(
    items.map((theme) => normalizeGeneratedApprovalTheme(theme))
  );
}

export async function createApprovalTheme(
  theme: CreateApprovalThemeInput
): Promise<ApprovalTheme> {
  const { data } = await skedApi.post<ApprovalTheme>(EXTERNAL_THEMES_URL, {
    name: theme.name,
    imageUrl: theme.imageUrl,
    companyName: theme.companyName,
  });
  return data;
}

export async function updateApprovalTheme(
  theme: ApprovalTheme
): Promise<ApprovalTheme> {
  await skedApi.put(`${EXTERNAL_THEMES_URL}/${theme._id}`, {
    name: theme.name,
    imageUrl: theme.imageUrl,
    companyName: theme.companyName,
  });
  return theme;
}

export async function deleteApprovalTheme(themeId: string): Promise<void> {
  await skedApi.delete(`${EXTERNAL_THEMES_URL}/${themeId}`);
}

export async function listApprovalLinks(): Promise<ApprovalLink[]> {
  const items = await fetchGeneratedApprovalsPages({
    path: "/v2/external-approvals",
    limit: EXTERNAL_APPROVAL_LINKS_LIMIT,
  });
  return sortApprovalLinksByName(
    items.map((link) => normalizeGeneratedApprovalLink(link))
  );
}

export async function createApprovalLink(
  link: CreateApprovalLinkInput
): Promise<ApprovalLink> {
  const createPayload = { ...link } as CreateApprovalLinkInput & {
    _id?: unknown;
    slugId?: unknown;
  };
  delete createPayload._id;
  delete createPayload.slugId;
  const { data } = await skedApi.post<ApprovalLink>(
    EXTERNAL_LINKS_URL,
    createPayload
  );
  return normalizeApprovalLink(data);
}

export async function updateApprovalLink(
  link: ApprovalLink
): Promise<ApprovalLink> {
  const { data } = await skedApi.put<ApprovalLink>(
    `${EXTERNAL_LINKS_URL}/${link._id}`,
    link
  );
  return normalizeApprovalLink(data);
}

export async function deleteApprovalLink(linkId: string): Promise<void> {
  await skedApi.delete(`${EXTERNAL_LINKS_URL}/${linkId}`);
}

export async function fetchApprovalBoardPosts(
  payload: ApprovalBoardPostsRequest
): Promise<ApprovalBoardPostsData> {
  const result = await fetchCalendar(payload);
  const posts = await Promise.all(
    (result?.data?.posts || []).map((post) =>
      standardizationPostData(post as unknown as Post)
    )
  );
  return indexApprovalBoardPosts(posts as CalendarPostType[]);
}

export async function fetchApprovalBoardDraftPosts(payload: {
  accountIds: string[];
  postContentTypes: string[];
  postStatusKey: string[] | null;
}): Promise<ApprovalBoardPostsData> {
  const { data } = await axios.post<{
    schedules?: ApprovalBoardDraftRow[];
  }>("/schedules/drafts", {
    page: 1,
    pageSize: 1000,
    order: "desc",
    "accountIds[]": payload.accountIds,
    postContentTypes: payload.postContentTypes,
    postStatusKey: payload.postStatusKey,
  });

  const posts = await Promise.all(
    (data.schedules ?? []).map((draft) => {
      const schedule = draft.schedule ?? {};
      return standardizationPostData({
        ...schedule,
        _id: draft._id,
        accountIds: draft.accountIds ?? [],
        postType: "draft",
        queued: false,
      } as unknown as Post);
    })
  );

  return indexApprovalBoardPosts(posts as CalendarPostType[]);
}

export async function updateApprovalPostStatus(payload: {
  postIds: string[];
  postType: string;
  statusKey: string;
}): Promise<void> {
  await axios.put("/collaboration/status", payload);
}

export async function cancelApprovalBoardPost(post: ApprovalBoardPost) {
  const accountId = getApprovalBoardPostAccountIds(post)[0];
  if (!accountId) {
    throw new Error("Cannot cancel a post without an account id.");
  }

  const originalPostType = getApprovalBoardPostType(post);
  if (originalPostType !== "schedule" && originalPostType !== "queue") {
    throw new Error("Only scheduled and queued posts can be cancelled.");
  }

  await cancelPlannerPost(accountId, {
    _id: post._id,
    originalPostType,
  });
}

export async function revertApprovalBoardPostToDraft(post: ApprovalBoardPost) {
  const postType = getApprovalBoardPostType(post);

  if (postType === "schedule") {
    await moveScheduleToDraft(post._id);
    return;
  }

  if (postType === "queue") {
    const newId = await moveQueueToDraft(post._id);
    await editDraft({
      ...toLegacyApprovalPost(post),
      _id: newId,
    });
    return;
  }

  throw new Error("Only scheduled and queued posts can be reverted to draft.");
}

export async function bulkRevertApprovalBoardPostsToDraft(
  posts: CalendarPostType[]
): Promise<string[]> {
  const idGroups = posts.reduce(
    (groups, post) => {
      const postType = getApprovalBoardPostType(post);
      if (postType === "queue") {
        groups.qPostIds.push(post._id);
      } else if (postType === "schedule") {
        groups.postIds.push(post._id);
      } else {
        throw new Error(
          "Only scheduled and queued posts can be reverted to draft."
        );
      }

      return groups;
    },
    { postIds: [] as string[], qPostIds: [] as string[] }
  );

  const { data } = await axios.post<string[]>("/posts/bulk/drafts", idGroups);
  return data;
}

export async function fetchApprovalComments(
  channelId: string
): Promise<ColComments> {
  const { data } = await collabApi.getComments(channelId);
  return data;
}

export async function fetchApprovalCollaborators(): Promise<ColCollaborator[]> {
  const { data } = await collabApi.getCollaborators();
  return data;
}

export async function addApprovalComment(
  channelId: string,
  payload: {
    body: string;
    metadata: {
      post: { postId: string };
      postType: string;
    };
    visibility: ColCommentVisibility;
  }
) {
  await collabApi.addComment(channelId, payload);
}

export async function editApprovalComment(
  channelId: string,
  commentId: string,
  payload: { body: string; visibility: ColCommentVisibility }
) {
  await collabApi.editComment(channelId, commentId, payload);
}

export async function deleteApprovalComment(
  channelId: string,
  commentId: string
) {
  await collabApi.deleteComment(channelId, commentId);
}

function invalidateExternalApprovals(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: queryKeys.approvals.external.all,
  });
}

function handleMutationError(error: unknown) {
  handleError(error);
}

const handledExternalQueryErrors = new WeakSet<object>();

function handleExternalQueryError(error: unknown) {
  if (typeof error === "object" && error !== null) {
    if (handledExternalQueryErrors.has(error)) return;
    handledExternalQueryErrors.add(error);
  }

  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 401 || status === 403) {
    removeLocalStorageSkedApiToken();
  }
  handleError(error);
}

export function useApprovalThemes() {
  const user = useSelector(getUser);
  const canAccessExternalApprovals = hasExternalApprovals(user);
  const query = useQuery({
    queryKey: queryKeys.approvals.external.themes,
    queryFn: listApprovalThemes,
    enabled: canAccessExternalApprovals,
  });

  useEffect(() => {
    if (!canAccessExternalApprovals || !query.error) return;
    handleExternalQueryError(query.error);
  }, [canAccessExternalApprovals, query.error]);

  // A collaborator's theme visibility is derived from their visible links, so
  // theme loading remains pending until link visibility is authoritative.
  const { data: visibleLinks = [], isLoading: isLinksVisibilityLoading } =
    useApprovalLinks();
  const visibleThemes = useMemo(
    () =>
      canAccessExternalApprovals
        ? filterApprovalThemesByVisibleLinks(
            query.data ?? [],
            visibleLinks,
            user?.role === "admin"
          )
        : [],
    [canAccessExternalApprovals, query.data, user?.role, visibleLinks]
  );

  return {
    ...query,
    data: visibleThemes,
    error: canAccessExternalApprovals ? query.error : null,
    isError: canAccessExternalApprovals && query.isError,
    isFetching: canAccessExternalApprovals && query.isFetching,
    isLoading:
      canAccessExternalApprovals &&
      (query.isLoading || isLinksVisibilityLoading),
  };
}

export function useApprovalLinks() {
  const user = useSelector(getUser);
  const canAccessExternalApprovals = hasExternalApprovals(user);
  const query = useQuery({
    queryKey: queryKeys.approvals.external.links,
    queryFn: listApprovalLinks,
    enabled: canAccessExternalApprovals,
  });

  useEffect(() => {
    if (!canAccessExternalApprovals || !query.error) return;
    handleExternalQueryError(query.error);
  }, [canAccessExternalApprovals, query.error]);

  const accounts = useSelector(getAccounts);
  const accountsReady = useSelector(getAccountsReady);
  const isAccountAccessPending =
    canAccessExternalApprovals && user?.role !== "admin" && !accountsReady;
  // The Redux account list comes from the collaborator-scoped /accounts
  // response and is the client-side visibility set; server authorization
  // remains authoritative for every approval operation.
  const visibleLinks = useMemo(
    () =>
      canAccessExternalApprovals
        ? filterApprovalLinksByAccountAccess(
            query.data ?? [],
            accounts.map((account) => account._id),
            user?.role === "admin",
            accountsReady
          )
        : [],
    [
      accounts,
      accountsReady,
      canAccessExternalApprovals,
      query.data,
      user?.role,
    ]
  );

  return {
    ...query,
    data: visibleLinks,
    error: canAccessExternalApprovals ? query.error : null,
    isError: canAccessExternalApprovals && query.isError,
    isFetching: canAccessExternalApprovals && query.isFetching,
    isLoading:
      canAccessExternalApprovals && (query.isLoading || isAccountAccessPending),
  };
}

export function useApprovalThemeMutations() {
  const queryClient = useQueryClient();
  const createTheme = useMutation<
    ApprovalTheme,
    unknown,
    CreateApprovalThemeInput
  >({
    mutationFn: createApprovalTheme,
    onSuccess: () => invalidateExternalApprovals(queryClient),
    onError: handleMutationError,
  });
  const updateTheme = useMutation<ApprovalTheme, unknown, ApprovalTheme>({
    mutationFn: updateApprovalTheme,
    onSuccess: () => invalidateExternalApprovals(queryClient),
    onError: handleMutationError,
  });
  const deleteTheme = useMutation<void, unknown, string>({
    mutationFn: deleteApprovalTheme,
    onSuccess: () => invalidateExternalApprovals(queryClient),
    onError: handleMutationError,
  });

  return { createTheme, updateTheme, deleteTheme };
}

export function useApprovalLinkMutations() {
  const queryClient = useQueryClient();
  const createLink = useMutation<
    ApprovalLink,
    unknown,
    CreateApprovalLinkInput
  >({
    mutationFn: createApprovalLink,
    onSuccess: () => invalidateExternalApprovals(queryClient),
    onError: handleMutationError,
  });
  const updateLink = useMutation<ApprovalLink, unknown, ApprovalLink>({
    mutationFn: updateApprovalLink,
    onSuccess: () => invalidateExternalApprovals(queryClient),
    onError: handleMutationError,
  });
  const deleteLink = useMutation<void, unknown, string>({
    mutationFn: deleteApprovalLink,
    onSuccess: () => invalidateExternalApprovals(queryClient),
    onError: handleMutationError,
  });

  return { createLink, updateLink, deleteLink };
}

export function useApprovalExternalData() {
  const linksQuery = useApprovalLinks();
  const themesQuery = useApprovalThemes();

  return {
    links: linksQuery.data ?? [],
    themes: themesQuery.data ?? [],
    themesById: useMemo(
      () =>
        Object.fromEntries(
          (themesQuery.data ?? []).map((theme) => [theme._id, theme])
        ),
      [themesQuery.data]
    ),
    isLoading: linksQuery.isLoading || themesQuery.isLoading,
    isFetching: linksQuery.isFetching || themesQuery.isFetching,
    isError: linksQuery.isError || themesQuery.isError,
    linksQuery,
    themesQuery,
  };
}

export function useApprovalComments(channelId: string | null) {
  return useQuery({
    queryKey: channelId
      ? queryKeys.approvals.comments(channelId)
      : ["approvals", "comments", "none"],
    queryFn: () => fetchApprovalComments(channelId as string),
    enabled: !!channelId,
  });
}

export function useApprovalCollaborators() {
  return useQuery({
    queryKey: queryKeys.approvals.collaborators,
    queryFn: fetchApprovalCollaborators,
  });
}

export function updateApprovalBoardPostInCache(
  queryClient: QueryClient,
  postId: string,
  changes: Record<string, unknown>
): boolean {
  let didPatch = false;
  const queries = queryClient.getQueriesData<ApprovalBoardPostsData>({
    queryKey: queryKeys.approvals.boardPosts.all,
  });

  for (const [queryKey, data] of queries) {
    if (!data?.postById[postId]) continue;
    didPatch = true;
    const statusFilter = getApprovalBoardStatusFilterFromQueryKey(queryKey);
    const nextStatusKey = changes.postStatusKey;
    const shouldRemoveFromQuery =
      typeof nextStatusKey === "string" &&
      statusFilter.length > 0 &&
      !statusFilter.includes(nextStatusKey);

    queryClient.setQueryData<ApprovalBoardPostsData>(queryKey, () => {
      if (shouldRemoveFromQuery) {
        return indexApprovalBoardPosts(
          data.posts.filter((post) => post._id !== postId)
        );
      }
      const posts = data.posts.map((post) => {
        if (post._id !== postId) return post;
        return {
          ...(post as object),
          ...changes,
        } as unknown as CalendarPostType;
      });
      return indexApprovalBoardPosts(posts);
    });
  }

  return didPatch;
}

export function removeApprovalBoardPostFromCache(
  queryClient: QueryClient,
  postId: string
) {
  queryClient.setQueriesData<ApprovalBoardPostsData>(
    { queryKey: queryKeys.approvals.boardPosts.all },
    (data) => {
      if (!data?.postById[postId]) return data;
      return indexApprovalBoardPosts(
        data.posts.filter((post) => post._id !== postId)
      );
    }
  );
}

export function upsertApprovalCommentInCache(
  queryClient: QueryClient,
  channelId: string,
  comment: ColComment,
  activeUserId?: string
) {
  const commentForCache =
    activeUserId && !comment.readBy.includes(activeUserId)
      ? { ...comment, readBy: [...comment.readBy, activeUserId] }
      : comment;

  queryClient.setQueryData<ColComments>(
    queryKeys.approvals.comments(channelId),
    (data) => {
      const current = data ?? { channelId, activeThread: [], legacyThread: [] };
      const activeThread = current.activeThread ?? [];
      const exists = activeThread.some(
        (item) => item._id === commentForCache._id
      );
      return {
        ...current,
        activeThread: exists
          ? activeThread.map((item) =>
              item._id === commentForCache._id
                ? { ...item, ...commentForCache }
                : item
            )
          : [...activeThread, commentForCache],
      };
    }
  );
}
